import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import Draggable from "react-draggable";
import Custom from "../../assets/images/Custom-shirt.webp";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import Switcher from "../../components/switcher";
import ScrollToTop from "../../components/scroll-to-top";
import BottomNav from "../../components/bottomnav.jsx";
import { CartContext } from "../../components/CartContext.jsx";
import { addToCartApi } from "../../ApiService/addtocartapi/addtocartapi";
import { FiX, FiPlus } from "../../assets/icons/vander";

const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_BASE_URL;
const UNIVERSAL_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

export default function Customize() {
  const navigate = useNavigate();
  const location = useLocation();
  const product = location.state?.product;
  const { addToCart } = useContext(CartContext);
  const logoFileInputRef = useRef(null);

  const [logos, setLogos] = useState([]);
  const [activeLogoId, setActiveLogoId] = useState(null);
  const [customText, setCustomText] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState("20 Pcs");
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [showShareSize, setShowShareSize] = useState(
    !location.state?.hideShareSize
  );
  const [showSelection, setShowSelection] = useState(
    !!location.state?.hideSelection
  );
  const [productCombinations, setProductCombinations] = useState([]);
  const [sizeQuantities, setSizeQuantities] = useState({});
  const [imageSrc, setImageSrc] = useState(
    product?.main_image || product?.variants?.[0]?.images?.[0]
      ? `${BASE_MEDIA_URL}${
          product.main_image || product.variants[0].images[0]
        }`
      : Custom
  );
  const [imageType, setImageType] = useState("product");
  const [overlaySize, setOverlaySize] = useState({ width: 450, height: 550 });
  const [selectedColor, setSelectedColor] = useState(
    product?.variants?.[0]?.color_code || "#ffffff"
  );
  const [selectedSize, setSelectedSize] = useState(
    product?.variants?.[0]?.sizes?.[0]?.size || "M"
  );
  const [selectedFabric, setSelectedFabric] = useState(
    product?.variants?.[0]?.fabric || ""
  );
  const [selectedGender, setSelectedGender] = useState(
    product?.variants?.[0]?.gender || ""
  );
  const [currentVariant, setCurrentVariant] = useState(
    product?.variants?.[0] || null
  );
  const [customSizes, setCustomSizes] = useState([
    { id: Date.now(), size: "", quantity: "1 Pcs" },
  ]);
  const [pincode, setPincode] = useState("");
  const [availabilityInfo, setAvailabilityInfo] = useState(null);
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);

  const imageContainerRef = useRef(null);
  const productFileInputRef = useRef(null);
  const overlayRef = useRef(null);

  const [isPincodeValid, setIsPincodeValid] = useState(false);
  const [pincodeError, setPincodeError] = useState(null);

  // Memoized variant values
  const variantColors = useMemo(
    () =>
      product?.variants?.map((variant) => ({
        name: variant.color,
        value: variant.color_code,
      })) || [],
    [product]
  );

  const availableFabrics = useMemo(
    () =>
      product?.variants
        ?.map((variant) => variant.fabric)
        .filter((fabric, index, self) => self.indexOf(fabric) === index) || [],
    [product]
  );

  const availableGenders = useMemo(
    () =>
      product?.variants
        ?.map((variant) => variant.gender)
        .filter((gender, index, self) => self.indexOf(gender) === index) || [],
    [product]
  );

  const availableSizes = useMemo(
    () => currentVariant?.sizes?.map((size) => size.size) || [],
    [currentVariant]
  );

  // Combined useEffect for variant and image updates
  useEffect(() => {
    if (!product?.variants || !showSelection) return;

    const variant = product.variants.find(
      (v) =>
        v.color_code === selectedColor &&
        v.fabric === selectedFabric &&
        v.gender === selectedGender
    );

    if (variant) {
      setCurrentVariant(variant);
      if (imageType === "product" && variant.images?.length) {
        setImageSrc(`${BASE_MEDIA_URL}${variant.images[0]}`);
      }
      if (
        variant.sizes &&
        !variant.sizes.some((s) => s.size === selectedSize)
      ) {
        setSelectedSize(variant.sizes[0]?.size || "");
      }
      setSizeQuantities({});
      setProductCombinations([]);
    } else {
      setCurrentVariant(null);
      setSelectedSize("");
      setSizeQuantities({});
      setProductCombinations([]);
    }
  }, [
    selectedColor,
    selectedFabric,
    selectedGender,
    showSelection,
    product,
    selectedSize,
    imageType,
  ]);

  // Handle image container resize
  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node) return;

    const update = () => {
      const rect = node.getBoundingClientRect();
      setOverlaySize({ width: rect.width, height: rect.height });
    };
    update();

    const ro = window.ResizeObserver ? new ResizeObserver(update) : null;
    if (ro) ro.observe(node);
    else window.addEventListener("resize", update);

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  // Handle pincode API call
  useEffect(() => {
    if (pincode.length === 6) {
      setIsLoadingPincode(true);
      fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        .then((response) => response.json())
        .then((data) => {
          setIsLoadingPincode(false);
          if (data && data.length > 0 && data[0].Status === "Success") {
            const postOffices = data[0].PostOffice;
            if (postOffices && postOffices.length > 0) {
              const firstPostOffice = postOffices[0];
              setAvailabilityInfo({
                name: firstPostOffice.Name,
                district: firstPostOffice.District,
                state: firstPostOffice.State,
                country: firstPostOffice.Country,
              });
            } else {
              setAvailabilityInfo({
                error: "No post offices found for this pincode",
              });
            }
          } else {
            setAvailabilityInfo({ error: "Invalid pincode" });
          }
        })
        .catch((error) => {
          setIsLoadingPincode(false);
          setAvailabilityInfo({ error: "Error fetching pincode details" });
          console.error("Error fetching pincode details:", error);
        });
    } else {
      setAvailabilityInfo(null);
    }
  }, [pincode]);

  const handleFileChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      const newLogos = files.map((file, index) => {
        const logoSize = Math.min(100, overlaySize.width * 0.3);
        const centeredX = Math.max(0, (overlaySize.width - logoSize) / 2);
        const centeredY = Math.max(0, (overlaySize.height - logoSize) / 2);
        return {
          id: Date.now() + index,
          src: URL.createObjectURL(file),
          fileName: file.name,
          file,
          size: logoSize,
          position: { x: centeredX, y: centeredY },
          rotation: 0,
        };
      });
      setLogos((prev) => [...prev, ...newLogos]);
    },
    [overlaySize]
  );

  const handleProductImageChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (file) {
        if (imageSrc && imageType === "custom") URL.revokeObjectURL(imageSrc);
        const newImageUrl = URL.createObjectURL(file);
        setImageSrc(newImageUrl);
        setImageType("custom");
        setShowSelection(false);
        setShowShareSize(true);
        setSizeQuantities({});
        setProductCombinations([]);
        setCustomText("");
        setSelectedColor("#ffffff");
        setSelectedFabric("");
        setSelectedGender("");
        setSelectedSize("M");
        setCurrentVariant(null);
        setCustomSizes([{ id: Date.now(), size: "", quantity: "1 Pcs" }]);
      }
    },
    [imageSrc, imageType]
  );

  const handleRemoveLogo = useCallback(
    (id) => {
      setLogos((prev) => {
        const logo = prev.find((l) => l.id === id);
        if (logo) URL.revokeObjectURL(logo.src);
        return prev.filter((l) => l.id !== id);
      });
      if (activeLogoId === id) setActiveLogoId(null);
    },
    [activeLogoId]
  );

  const handleDrag = useCallback(
    (id, _, data) => {
      if (!draggingHandle) {
        setLogos((prev) =>
          prev.map((logo) =>
            logo.id === id
              ? { ...logo, position: { x: data.x, y: data.y } }
              : logo
          )
        );
      }
    },
    [draggingHandle]
  );

  const handleLogoClick = useCallback((id) => {
    setActiveLogoId(id);
  }, []);

  const handleLogoUploadClick = useCallback(() => {
    logoFileInputRef.current?.click();
  }, []);

  const handleInteractionStart = useCallback(
    (e, id, handleType) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingHandle(handleType);

      const logo = logos.find((l) => l.id === id);
      if (!logo) return;

      const container = document.querySelector(`.logo-${id}`);
      if (!container) return;

      const isTouch = e.type === "touchstart";
      const startX = isTouch ? e.touches[0].clientX : e.clientX;
      const startY = isTouch ? e.touches[0].clientY : e.clientY;
      const startSize = logo.size;
      const startRotation = logo.rotation;

      const handleInteractionMove = (moveEvent) => {
        moveEvent.preventDefault();
        const clientX =
          moveEvent.type === "touchmove"
            ? moveEvent.touches[0].clientX
            : moveEvent.clientX;
        const clientY =
          moveEvent.type === "touchmove"
            ? moveEvent.touches[0].clientY
            : moveEvent.clientY;

        if (handleType === "resize") {
          const deltaX = clientX - startX;
          const newSize = Math.max(
            20,
            Math.min(overlaySize.width * 0.5, startSize + deltaX)
          );
          setLogos((prev) =>
            prev.map((l) => (l.id === id ? { ...l, size: newSize } : l))
          );
        } else if (handleType === "rotate") {
          const rect = container.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const deltaX = clientX - centerX;
          const deltaY = clientY - centerY;
          const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
          const newRotation = (angle + 90 + 360) % 360;
          setLogos((prev) =>
            prev.map((l) => (l.id === id ? { ...l, rotation: newRotation } : l))
          );
        }
      };

      const handleInteractionEnd = () => {
        setDraggingHandle(null);
        document.removeEventListener("mousemove", handleInteractionMove);
        document.removeEventListener("mouseup", handleInteractionEnd);
        document.removeEventListener("touchmove", handleInteractionMove);
        document.removeEventListener("touchend", handleInteractionEnd);
      };

      if (isTouch) {
        document.addEventListener("touchmove", handleInteractionMove, {
          passive: false,
        });
        document.addEventListener("touchend", handleInteractionEnd);
      } else {
        document.addEventListener("mousemove", handleInteractionMove);
        document.addEventListener("mouseup", handleInteractionEnd);
      }
    },
    [logos, overlaySize]
  );

  const generateCustomizedImage = useCallback(() => {
    return new Promise((resolve) => {
      if (!imageSrc) return resolve(null);

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const baseImg = new Image();
      baseImg.src = imageSrc;
      baseImg.crossOrigin = "anonymous";
      baseImg.onload = () => {
        canvas.width = baseImg.width;
        canvas.height = baseImg.height;
        ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

        const loadLogos = logos.map(
          (logo) =>
            new Promise((logoResolve) => {
              const logoImg = new Image();
              logoImg.src = logo.src;
              logoImg.crossOrigin = "anonymous";
              logoImg.onload = () => {
                const scaleX = canvas.width / overlaySize.width;
                const scaleY = canvas.height / overlaySize.height;
                ctx.save();
                ctx.translate(
                  (logo.position.x + logo.size / 2) * scaleX,
                  (logo.position.y + logo.size / 2) * scaleY
                );
                ctx.rotate((logo.rotation * Math.PI) / 180);
                ctx.drawImage(
                  logoImg,
                  (-logo.size / 2) * scaleX,
                  (-logo.size / 2) * scaleY,
                  logo.size * scaleX,
                  logo.size * scaleY
                );
                ctx.restore();
                logoResolve();
              };
              logoImg.onerror = () => logoResolve();
            })
        );

        Promise.all(loadLogos).then(() => {
          canvas.toBlob(
            (blob) =>
              resolve(
                blob
                  ? new File([blob], "customized-product.png", {
                      type: "image/png",
                    })
                  : null
              ),
            "image/png"
          );
        });
      };
      baseImg.onerror = () => resolve(null);
    });
  }, [imageSrc, logos, overlaySize]);

  const createFormData = useCallback(
    async (productId, customerId, variantDetails) => {
      const formData = new FormData();
      formData.append("product_id", productId);
      formData.append("customer_id", customerId);
      formData.append("custom_text", customText);
      formData.append("is_product", false);
      logos.forEach((logo) => formData.append("logos", logo.file));
      formData.append(
        "items",
        JSON.stringify(
          variantDetails.map(({ sku_code, size, quantity }) => ({
            sku_code,
            size,
            quantity,
          }))
        )
      );
      const customizedImage = await generateCustomizedImage();
      if (customizedImage) formData.append("reference_image", customizedImage);
      return formData;
    },
    [customText, logos, generateCustomizedImage]
  );

  const createCartItem = useCallback(
    (productId, price, quantity, size, combination = {}) => ({
      id: productId || Date.now() + (combination.id || 0),
      name:
        imageType === "custom"
          ? "Customized Product"
          : product?.name || "Customized Polo T-Shirt",
      image: imageSrc,
      amount: price,
      quantity,
      color: selectedColor,
      colorName: variantColors.find((c) => c.value === selectedColor)?.name,
      size,
      fabric: selectedFabric,
      gender: selectedGender,
      logos: logos.map(({ id, src, fileName, size, position, rotation }) => ({
        id,
        src,
        fileName,
        size,
        position,
        rotation,
      })),
      customText,
    }),
    [
      imageSrc,
      imageType,
      product,
      selectedColor,
      selectedFabric,
      selectedGender,
      customText,
      logos,
      variantColors,
    ]
  );

  // Consolidated add to cart function
  const handleAddToCart = useCallback(
    async (e, isAddingAll = false) => {
      if (e) e.stopPropagation();

      // Validation for logo upload
      if (logos.length === 0) {
        return toast.error(
          "Please upload at least one logo before adding to cart.",
          {
            style: { borderRadius: "10px", background: "#333", color: "#fff" },
          }
        );
      }

      const customerId = localStorage.getItem("customer_id");
      if (!customerId) {
        return toast.error("Please log in to add items to the cart.", {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      }

      const loadingToastId = toast.loading("Adding product to cart...");

      try {
        let variantDetails = [];
        let productId = "";

        if (imageType === "custom") {
          // Handle custom product (uploaded image)
          const hasValidSizes = customSizes.some(
            (item) => item.size && item.quantity
          );
          if (!hasValidSizes) {
            toast.dismiss(loadingToastId);
            return toast.error(
              "Please select at least one size and quantity.",
              {
                style: {
                  borderRadius: "10px",
                  background: "#333",
                  color: "#fff",
                },
              }
            );
          }

          variantDetails = customSizes
            .filter((item) => item.size && item.quantity)
            .map((item) => ({
              size: item.size,
              quantity: parseInt(item.quantity.split(" ")[0]) || 1,
              sku_code: "", // Empty sku_code for custom products
            }));

          // Use empty string for product_id as per requirement
          productId = "";
        } else {
          // Handle regular product
          if (isAddingAll) {
            // Adding all combinations
            if (!productCombinations.length) {
              toast.dismiss(loadingToastId);
              return toast.error("Please add at least one combination!");
            }

            productId = product.product_id;
            variantDetails = productCombinations.map((combination) => {
              const selectedVariant = product.variants.find(
                (v) => v.color_code === combination.colorValue
              );
              const selectedSizeData = selectedVariant?.sizes?.find(
                (s) => s.size === combination.size
              );
              const skuCode = selectedSizeData?.sku || "default-sku";
              const quantityValue =
                parseInt(combination.quantity.split(" ")[0]) || 20;

              return {
                color: combination.color,
                color_code: combination.colorValue,
                fabric: combination.fabric,
                gender: combination.gender,
                size: combination.size,
                sku_code: skuCode,
                quantity: quantityValue,
              };
            });
          } else {
            // Adding single product
            if (!showShareSize && !Object.keys(sizeQuantities).length) {
              toast.dismiss(loadingToastId);
              return toast.error(
                "Please select at least one size and quantity.",
                {
                  style: {
                    borderRadius: "10px",
                    background: "#333",
                    color: "#fff",
                  },
                }
              );
            }
            if (showShareSize && !selectedSize) {
              toast.dismiss(loadingToastId);
              return toast.error("Please select a size.", {
                style: {
                  borderRadius: "10px",
                  background: "#333",
                  color: "#fff",
                },
              });
            }

            productId = product.product_id;

            if (showShareSize) {
              const quantityValue =
                parseInt(selectedQuantity.split(" ")[0]) || 20;
              const selectedSizeData = currentVariant?.sizes?.find(
                (s) => s.size === selectedSize
              );
              const skuCode = selectedSizeData?.sku || "default-sku";

              variantDetails.push({
                color:
                  variantColors.find((c) => c.value === selectedColor)?.name ||
                  "Unknown",
                color_code: selectedColor,
                fabric: selectedFabric,
                gender: selectedGender,
                size: selectedSize,
                sku_code: skuCode,
                quantity: quantityValue,
              });
            } else {
              Object.entries(sizeQuantities).forEach(([size, qty]) => {
                const quantityValue = parseInt(qty.split(" ")[0]) || 20;
                const selectedSizeData = currentVariant?.sizes?.find(
                  (s) => s.size === size
                );
                const skuCode = selectedSizeData?.sku || "default-sku";

                variantDetails.push({
                  color:
                    variantColors.find((c) => c.value === selectedColor)
                      ?.name || "Unknown",
                  color_code: selectedColor,
                  fabric: selectedFabric,
                  gender: selectedGender,
                  size,
                  sku_code: skuCode,
                  quantity: quantityValue,
                });
              });
            }
          }
        }

        // Create and send the form data
        const formData = await createFormData(
          productId,
          customerId,
          variantDetails
        );
        await addToCartApi(formData);

        // Show success message
        toast.dismiss(loadingToastId);
        if (imageType === "custom") {
          toast.success("Customized Product added to cart!", {
            icon: "🛒",
            style: { borderRadius: "10px", background: "#333", color: "#fff" },
          });
        } else if (isAddingAll) {
          toast.success("All products added to cart!");
        } else {
          toast.success(
            `${product?.name || "Customized Polo T-Shirt"} added to cart!`,
            {
              icon: "🛒",
              style: {
                borderRadius: "10px",
                background: "#333",
                color: "#fff",
              },
            }
          );
        }

        // Navigate to cart after a short delay
        setTimeout(() => navigate("/shop-cart"), 800);
      } catch (error) {
        console.log("error", error);
        toast.dismiss(loadingToastId);
        toast.error("Failed to add product to cart. Please try again.", {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      }
    },
    [
      product,
      selectedColor,
      selectedSize,
      selectedQuantity,
      sizeQuantities,
      showShareSize,
      createFormData,
      navigate,
      variantColors,
      imageType,
      selectedFabric,
      selectedGender,
      currentVariant,
      customSizes,
      logos,
      productCombinations,
    ]
  );

  const handleCheckPincode = useCallback(async () => {
  if (pincode.length !== 6) return;
  
  setIsLoadingPincode(true);
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/check-pincode/?pincode=${pincode}`
    );
    const data = await response.json();
    
    if (data.is_available) {
      setIsPincodeValid(true);
      setPincodeError(null);
      
      // Update availability info with delivery details
      setAvailabilityInfo({
        deliveryTime: data.delivery_time,
        deliveryCharges: data.delivery_charges,
        isAvailable: true
      });
    } else {
      setIsPincodeValid(false);
      setPincodeError("Delivery not available at this pincode");
      
      // Update availability info to show not available
      setAvailabilityInfo({
        error: "Delivery not available at this pincode",
        isAvailable: false
      });
    }
  } catch (error) {
    console.error("Error checking pincode:", error);
    setIsPincodeValid(false);
    setPincodeError("Error checking pincode. Please try again.");
    setAvailabilityInfo({
      error: "Error checking pincode. Please try again.",
      isAvailable: false
    });
  } finally {
    setIsLoadingPincode(false);
  }
}, [pincode]);

  const handleAddCombination = useCallback(
    (size, quantity) => {
      const colorName =
        variantColors.find((c) => c.value === selectedColor)?.name || "Unknown";
      const combination = {
        id: Date.now(),
        color: colorName,
        colorValue: selectedColor,
        size,
        fabric: selectedFabric || "N/A",
        gender: selectedGender || "N/A",
        quantity,
      };

      setProductCombinations((prev) => {
        const existingIndex = prev.findIndex(
          (item) =>
            item.color === combination.color &&
            item.size === combination.size &&
            item.fabric === combination.fabric &&
            item.gender === combination.gender
        );
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex].quantity = quantity;
          toast.success("Combination updated!");
          return updated;
        }
        toast.success("Combination added!");
        return [...prev, combination];
      });
    },
    [selectedColor, selectedFabric, selectedGender, variantColors]
  );

  const handleRemoveCombination = useCallback((id) => {
    setProductCombinations((prev) => prev.filter((item) => item.id !== id));
    toast.success("Combination removed!");
  }, []);

  const handleAddMoreProducts = useCallback(() => {
    setShowShareSize(false);
    setShowSelection(false);
    setProductCombinations([]);
    setCustomText("");
    setSizeQuantities({});
    if (imageSrc && imageType === "custom") URL.revokeObjectURL(imageSrc);
    setImageSrc(
      product?.main_image ? `${BASE_MEDIA_URL}${product.main_image}` : Custom
    );
    setImageType("product");
    setSelectedColor(product?.variants?.[0]?.color_code || "#ffffff");
    setSelectedFabric(product?.variants?.[0]?.fabric || "");
    setSelectedGender(product?.variants?.[0]?.gender || "");
    setSelectedSize(product?.variants?.[0]?.sizes?.[0]?.size || "M");
    setCurrentVariant(product?.variants?.[0] || null);
    navigate("/shop-grid-left-sidebar/allproducts");
  }, [imageSrc, imageType, product, navigate]);

  const handleUploadProductClick = useCallback(() => {
    productFileInputRef.current?.click();
  }, []);

  const handleSizeCheckboxChange = useCallback(
    (size) => {
      setSizeQuantities((prev) => {
        const newQuantities = { ...prev };
        if (newQuantities[size]) {
          delete newQuantities[size];
        } else {
          newQuantities[size] = "1 Pcs";
          handleAddCombination(size, "1 Pcs");
        }
        return newQuantities;
      });
    },
    [handleAddCombination]
  );

  const handleSizeQuantityChange = useCallback(
    (size, newQuantity) => {
      setSizeQuantities((prev) => ({ ...prev, [size]: newQuantity }));
      handleAddCombination(size, newQuantity);
    },
    [handleAddCombination]
  );

  const handleAddCustomSize = useCallback(() => {
    setCustomSizes((prev) => [
      ...prev,
      { id: Date.now(), size: "", quantity: "1 Pcs" },
    ]);
  }, []);

  const handleCustomSizeChange = useCallback(
    (id, size) => {
      setCustomSizes((prev) => {
        const updatedSizes = prev.map((item) =>
          item.id === id ? { ...item, size } : item
        );
        const changedItem = updatedSizes.find((item) => item.id === id);
        if (changedItem && changedItem.size) {
          handleAddCombination(changedItem.size, changedItem.quantity);
        }
        return updatedSizes;
      });
    },
    [handleAddCombination]
  );

  const handleCustomQuantityChange = useCallback(
    (id, quantity) => {
      setCustomSizes((prev) => {
        const updatedSizes = prev.map((item) =>
          item.id === id ? { ...item, quantity } : item
        );
        const changedItem = updatedSizes.find((item) => item.id === id);
        if (changedItem && changedItem.size) {
          handleAddCombination(changedItem.size, changedItem.quantity);
        }
        return updatedSizes;
      });
    },
    [handleAddCombination]
  );

  const handleRemoveCustomSize = useCallback(
    (id) => {
      setCustomSizes((prev) => {
        const updatedSizes = prev.filter((item) => item.id !== id);
        const removedItem = prev.find((item) => item.id === id);
        if (removedItem && removedItem.size) {
          setProductCombinations((prevCombinations) =>
            prevCombinations.filter(
              (comb) =>
                !(
                  comb.size === removedItem.size &&
                  comb.colorValue === selectedColor &&
                  comb.fabric === selectedFabric &&
                  comb.gender === selectedGender
                )
            )
          );
        }
        return updatedSizes;
      });
    },
    [selectedColor, selectedFabric, selectedGender]
  );

  const getAvailableSizesForCustom = useCallback(
    (currentIndex) => {
      const selectedSizes = customSizes
        .filter((_, index) => index !== currentIndex)
        .map((item) => item.size)
        .filter(Boolean);
      return UNIVERSAL_SIZES.filter((size) => !selectedSizes.includes(size));
    },
    [customSizes]
  );

  const handlePincodeChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 6 digits
    if (/^\d{0,6}$/.test(value)) {
      setPincode(value);
      // Reset validation states when pincode changes
      setIsPincodeValid(false);
      setPincodeError(null);
    }
  }, []);

  const renderHeader = () => (
    <>
      <Navbar navClass="defaultscroll is-sticky" navlight={true} />
      <section className="relative table w-full items-center py-36 bg-[url('../../assets/images/hero/pages.jpg')] bg-top bg-no-repeat bg-cover">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>
        <div className="container relative mx-auto px-4">
          <div className="grid grid-cols-1 pb-8 text-center mt-10">
            <h3 className="mb-3 text-2xl sm:text-3xl md:text-4xl font-semibold text-white">
              Customize
            </h3>
            <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
              Customized Your Product With Your Logo.
            </p>
          </div>
        </div>
        <div className="absolute text-center z-10 bottom-5 start-0 end-0 mx-3">
          <ul className="tracking-[0.5px] mb-0 inline-block">
            <li className="inline-block uppercase text-[11px] sm:text-[13px] font-bold duration-500 ease-in-out text-white/50 hover:text-white">
              <Link to="/">Sportyfi</Link>
            </li>
            <li className="inline-block text-base text-white/50 mx-0.5 ltr:rotate-0 rtl:rotate-180">
              <i className="mdi mdi-chevron-right"></i>
            </li>
            <li
              className="inline-block uppercase text-[11px] sm:text-[13px] font-bold duration-500 ease-in-out text-white"
              aria-current="page"
            >
              Customize
            </li>
          </ul>
        </div>
      </section>
    </>
  );

  const renderImageSection = () => (
    <div className="w-full max-w-[90vw] z-1 sm:max-w-[450px] mx-auto">
      <div
        className="relative aspect-[9/11] overflow-hidden"
        style={{ zIndex: 100 }}
      >
        {imageSrc ? (
          <div className="relative w-full h-full" ref={imageContainerRef}>
            <img
              src={imageSrc}
              alt={
                imageType === "custom"
                  ? "Customized Product Preview"
                  : product?.name || "Custom"
              }
              className="w-full h-full object-contain"
            />
            <div
              ref={overlayRef}
              className="absolute top-0 left-0 w-full h-full"
              style={{
                zIndex: 110,
                pointerEvents: logos.length > 0 ? "auto" : "none",
              }}
            >
              <div className="relative w-full h-full">
                {logos.map((logo) => (
                  <Draggable
                    key={logo.id}
                    bounds="parent"
                    position={logo.position}
                    onDrag={(e, data) => handleDrag(logo.id, e, data)}
                    onStop={(e, data) => handleDrag(logo.id, e, data)}
                    handle=".logo-image"
                  >
                    <div
                      className={`logo-container logo-${logo.id} absolute ${
                        activeLogoId === logo.id
                          ? "border-2 border-blue-500"
                          : ""
                      }`}
                      onClick={() => handleLogoClick(logo.id)}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        handleLogoClick(logo.id);
                      }}
                      style={{
                        width: logo.size,
                        height: logo.size,
                        zIndex: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "auto",
                        userSelect: "none",
                        touchAction: "none",
                      }}
                    >
                      <img
                        src={logo.src}
                        alt={`Logo ${logo.fileName}`}
                        className="logo-image cursor-move object-contain"
                        style={{
                          width: "100%",
                          height: "100%",
                          transform: `rotate(${logo.rotation}deg)`,
                          pointerEvents: "auto",
                        }}
                        draggable={false}
                      />
                      {activeLogoId === logo.id && (
                        <>
                          <div
                            className="absolute bottom-[-10px] right-[-10px] w-1 h-1 bg-blue-500 rounded-full cursor-se-resize z-20"
                            onMouseDown={(e) =>
                              handleInteractionStart(e, logo.id, "resize")
                            }
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              handleInteractionStart(e, logo.id, "resize");
                            }}
                          />
                          <div
                            className="absolute top-[-18px] left-1/2 transform -translate-x-1/2 w-1 h-1 bg-green-500 rounded-full cursor-pointer z-20"
                            onMouseDown={(e) =>
                              handleInteractionStart(e, logo.id, "rotate")
                            }
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              handleInteractionStart(e, logo.id, "rotate");
                            }}
                          />
                          <button
                            onClick={() => handleRemoveLogo(logo.id)}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo(logo.id);
                            }}
                            className="absolute right-[-6px] top-[-6px] text-red-500 hover:text-red-700 bg-white rounded-full p-1 z-20"
                            title="Remove Logo"
                          >
                            <FiX className="size-2" />
                          </button>
                        </>
                      )}
                    </div>
                  </Draggable>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-red-500 text-sm sm:text-base">
            Image not available
          </p>
        )}
      </div>
      <div
        className="w-full mt-4 bg-white p-4 rounded-lg z-[200]"
        style={{ position: "relative", minHeight: "100px" }}
      >
        {logos.find((logo) => logo.id === activeLogoId) && (
          <div className="space-y-2 mb-4">
            <p className="text-xs sm:text-sm text-gray-600">
              Size: {logos.find((logo) => logo.id === activeLogoId).size}px
            </p>
            <p className="text-xs sm:text-sm text-gray-600">
              Rotation:{" "}
              {logos.find((logo) => logo.id === activeLogoId).rotation}°
            </p>
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <button
            type="button"
            onClick={handleUploadProductClick}
            className="flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-4 sm:py-2 rounded-md bg-green-500 text-white font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-green-400 transition text-xs sm:text-sm md:text-base"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Upload Product</span>
          </button>
          <input
            ref={productFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleProductImageChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleAddMoreProducts}
            className="flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-4 sm:py-2 rounded-md bg-green-500 text-white font-medium hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition text-xs sm:text-sm md:text-base"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            <span>Add More Products</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderDetailsSection = () => {
    // Check if a product has been uploaded or selected
    const hasProduct = imageSrc && imageSrc !== Custom;

    // If no product has been uploaded/selected, show the simplified content
    if (!hasProduct) {
      return (
        <div className="w-full max-w-xl mx-auto space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Customize your product with your logo
          </h1>
          <p className="text-gray-700">
            Create unique, branded apparel by uploading your company logo and
            customizing it to match your style. Our easy-to-use customization
            tool allows you to position, resize, and rotate your logo to achieve
            the perfect look for your team or event.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-800 mb-2">Get Started</h3>
            <p className="text-blue-700">
              Please choose a product from our catalog or upload your own
              product image to begin customizing with your logo.
            </p>
          </div>
        </div>
      );
    }

    // If a product has been selected, show the full product details
    const selectedSizeData = currentVariant?.sizes?.find(
      (s) => s.size === selectedSize
    );
    const price =
      selectedSizeData?.price?.discounted ||
      selectedSizeData?.price?.mrp ||
      null;

    return (
      <div className="w-full max-w-xl mx-auto space-y-4 sm:space-y-6">
        <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
          {imageType === "custom"
            ? "Customized Product"
            : `Customized ${product?.name || "Polo T-Shirt"} With Your Logo`}
        </h1>
        {imageType === "product" && (
          <div className="flex items-center space-x-2 text-amber-400">
            <ul className="flex space-x-1">
              {[...Array(5)].map((_, i) => (
                <li key={i}>
                  <i
                    className={
                      i < Math.round(product?.ratings?.avg || 5)
                        ? "mdi mdi-star text-sm sm:text-base"
                        : "mdi mdi-star-outline text-sm sm:text-base"
                    }
                  ></i>
                </li>
              ))}
            </ul>
            <span className="text-gray-900 text-xs sm:text-sm">
              {product?.ratings?.avg || 4.3} ({product?.ratings?.count || 617}{" "}
              Reviews)
            </span>
          </div>
        )}
        {imageType != "custom" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 bg-gray-200 p-2 sm:p-3 rounded-md">
              <div className="flex items-center space-x-2 text-base sm:text-lg font-semibold">
                {price ? (
                  <>
                    <ins className="text-gray-900 no-underline">₹{price}</ins>
                    <span className="bg-red-100 text-red-600 text-xs sm:text-sm px-2 py-1 rounded">
                      SAVE 0%
                    </span>
                  </>
                ) : (
                  <span className="text-red-500">Price Not Available</span>
                )}
              </div>
            </div>
            <p className="text-gray-700 text-xs sm:text-sm">
              Inclusive of All Taxes
            </p>
          </>
        )}

        <div
          className="w-full p-3 sm:p-4 border-2 border-gray-300 rounded-lg flex flex-col items-center bg-green-500 hover:bg-green-400 text-white transition cursor-pointer"
          onClick={handleLogoUploadClick} // Use the ref-based handler
        >
          <label
            htmlFor="file-upload"
            className="font-medium text-sm sm:text-base cursor-pointer"
          >
            Upload Your Logos
          </label>
          <input
            ref={logoFileInputRef} // Use the ref
            id="file-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="customText"
            className="text-xs sm:text-sm font-medium text-gray-900 mb-2"
          >
            Any Text or Customization Needed (Optional)
          </label>
          <textarea
            id="customText"
            rows={2}
            placeholder="Write Here"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            className="w-full rounded-lg border border-gray-300 p-2 text-xs sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        {imageType === "custom" ? (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-lg font-semibold mb-4">
              Select Sizes and Quantities
            </h4>
            <div className="space-y-4">
              {customSizes.map((item, index) => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="w-[120px]">
                    {" "}
                    {/* Fixed width container for select */}
                    <select
                      value={item.size}
                      onChange={(e) =>
                        handleCustomSizeChange(item.id, e.target.value)
                      }
                      className="py-1 px-3 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Size</option>
                      {getAvailableSizesForCustom(index).map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  {item.size && (
                    <div className="flex items-center gap-2 ml-auto">
                      {" "}
                      {/* Added ml-auto to right-align */}
                      <div className="flex items-stretch gap-0 w-[140px]">
                        {" "}
                        {/* Increased width */}
                        <button
                          type="button"
                          onClick={() => {
                            const currentNum =
                              parseInt(item.quantity.split(" ")[0]) || 1;
                            if (currentNum > 1)
                              handleCustomQuantityChange(
                                item.id,
                                `${currentNum - 1} Pcs`
                              );
                          }}
                          className={`flex items-center justify-center px-2 py-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900 ${
                            parseInt(item.quantity.split(" ")[0]) <= 1
                              ? "opacity-50 cursor-not-allowed bg-gray-100 hover:bg-gray-100"
                              : ""
                          }`}
                          disabled={parseInt(item.quantity.split(" ")[0]) <= 1}
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 12H4"
                            />
                          </svg>
                        </button>
                        <div
                          className="flex items-center justify-center px-3 py-1 bg-white border-t border-b border-gray-300 text-gray-900 font-semibold min-w-[70px] text-center cursor-pointer hover:bg-gray-50 transition-colors relative"
                          onClick={() => {
                            const currentNum =
                              parseInt(item.quantity.split(" ")[0]) || 1;
                            const input = prompt(
                              `Enter quantity for size ${item.size}:`,
                              currentNum
                            );
                            if (input && !isNaN(input) && parseInt(input) >= 1)
                              handleCustomQuantityChange(
                                item.id,
                                `${parseInt(input)} Pcs`
                              );
                          }}
                        >
                          <span className="text-xs">
                            {parseInt(item.quantity.split(" ")[0])}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            Pcs
                          </span>
                          <svg
                            className="w-3 h-3 ml-1 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const currentNum =
                              parseInt(item.quantity.split(" ")[0]) || 1;
                            handleCustomQuantityChange(
                              item.id,
                              `${currentNum + 1} Pcs`
                            );
                          }}
                          className="flex items-center justify-center px-2 py-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-r-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                      {customSizes.length > 1 && (
                        <button
                          onClick={() => handleRemoveCustomSize(item.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiX className="size-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddCustomSize}
                className="flex items-center justify-center gap-1 px-3 py-2 border border-dashed border-gray-300 rounded-md text-gray-600 hover:text-gray-900 hover:border-gray-400 transition"
              >
                <FiPlus className="size-4" />
                <span>Add Another Size</span>
              </button>
            </div>
          </div>
        ) : showShareSize ? (
          <div className="relative">
            <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-3">
              Quantity:{" "}
              <span className="font-semibold text-indigo-600">
                {selectedQuantity}
              </span>
            </h4>
            <div className="flex items-stretch gap-0 max-w-xs">
              <button
                type="button"
                onClick={() => {
                  const currentNum =
                    parseInt(selectedQuantity.split(" ")[0]) || 1;
                  if (currentNum > 1) {
                    const newQuantity = `${currentNum - 1} Pcs`;
                    setSelectedQuantity(newQuantity);
                    handleAddCombination(selectedSize, newQuantity);
                  }
                }}
                className={`flex items-center justify-center px-4 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900 ${
                  parseInt(selectedQuantity.split(" ")[0]) <= 1
                    ? "opacity-50 cursor-not-allowed bg-gray-100 hover:bg-gray-100"
                    : ""
                }`}
                disabled={parseInt(selectedQuantity.split(" ")[0]) <= 1}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
              </button>
              <div
                className="flex items-center justify-center px-6 py-3 bg-white border-t border-b border-gray-300 text-gray-900 font-semibold min-w-[100px] text-center cursor-pointer hover:bg-gray-50 transition-colors relative"
                onClick={() => {
                  const currentNum =
                    parseInt(selectedQuantity.split(" ")[0]) || 1;
                  const input = prompt("Enter quantity:", currentNum);
                  if (input && !isNaN(input) && parseInt(input) >= 1) {
                    const newQuantity = `${parseInt(input)} Pcs`;
                    setSelectedQuantity(newQuantity);
                    handleAddCombination(selectedSize, newQuantity);
                  }
                }}
              >
                <span className="text-lg">
                  {parseInt(selectedQuantity.split(" ")[0])}
                </span>
                <span className="text-sm text-gray-500 ml-1">Pcs</span>
                <svg
                  className="w-4 h-4 ml-2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              <button
                type="button"
                onClick={() => {
                  const currentNum =
                    parseInt(selectedQuantity.split(" ")[0]) || 1;
                  const newQuantity = `${currentNum + 1} Pcs`;
                  setSelectedQuantity(newQuantity);
                  handleAddCombination(selectedSize, newQuantity);
                }}
                className="flex items-center justify-center px-4 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-r-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Minimum order: 1 piece</p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-lg font-semibold mb-2">
              Select Sizes and Quantities
            </h4>
            {availableSizes.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-red-500 font-medium mb-2">
                  No sizes available for this selection
                </div>
                <p className="text-gray-600 text-sm">
                  Please try a different color, fabric, or gender combination.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableSizes.map((size) => (
                  <div key={size} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`size-${size}`}
                        checked={!!sizeQuantities[size]}
                        onChange={() => handleSizeCheckboxChange(size)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label
                        htmlFor={`size-${size}`}
                        className="text-sm text-gray-900"
                      >
                        {size}
                      </label>
                    </div>
                    {sizeQuantities[size] && (
                      <div className="flex items-stretch gap-0 max-w-[120px]">
                        <button
                          type="button"
                          onClick={() => {
                            const currentNum =
                              parseInt(sizeQuantities[size].split(" ")[0]) || 1;
                            if (currentNum > 1)
                              handleSizeQuantityChange(
                                size,
                                `${currentNum - 1} Pcs`
                              );
                          }}
                          className={`flex items-center justify-center px-2 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900 ${
                            parseInt(sizeQuantities[size].split(" ")[0]) <= 1
                              ? "opacity-50 cursor-not-allowed bg-gray-100 hover:bg-gray-100"
                              : ""
                          }`}
                          disabled={
                            parseInt(sizeQuantities[size].split(" ")[0]) <= 1
                          }
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 12H4"
                            />
                          </svg>
                        </button>
                        <div
                          className="flex items-center justify-center px-3 py-1.5 bg-white border-t border-b border-gray-300 text-gray-900 font-semibold min-w-[60px] text-center cursor-pointer hover:bg-gray-50 transition-colors relative"
                          onClick={() => {
                            const currentNum =
                              parseInt(sizeQuantities[size].split(" ")[0]) || 1;
                            const input = prompt(
                              `Enter quantity for size ${size}:`,
                              currentNum
                            );
                            if (input && !isNaN(input) && parseInt(input) >= 1)
                              handleSizeQuantityChange(
                                size,
                                `${parseInt(input)} Pcs`
                              );
                          }}
                        >
                          <span className="text-xs">
                            {parseInt(sizeQuantities[size].split(" ")[0])}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            Pcs
                          </span>
                          <svg
                            className="w-3 h-3 ml-1 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const currentNum =
                              parseInt(sizeQuantities[size].split(" ")[0]) || 1;
                            handleSizeQuantityChange(
                              size,
                              `${currentNum + 1} Pcs`
                            );
                          }}
                          className="flex items-center justify-center px-2 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-r-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700 hover:text-gray-900"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {showSelection && (
          <>
            <div className="flex items-center mb-4">
              <h5 className="text-lg font-semibold me-2">Color:</h5>
              <div className="flex space-x-2">
                {variantColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => {
                      setSelectedColor(color.value);
                      setImageType("product");
                    }}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      selectedColor === color.value
                        ? "border-gray-800 scale-110 shadow-lg"
                        : "border-gray-300 hover:border-gray-500"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  ></button>
                ))}
              </div>
            </div>
            {availableFabrics.length > 0 && (
              <div className="flex items-center mb-4">
                <h5 className="text-lg font-semibold me-2">Fabric:</h5>
                <div className="flex space-x-2">
                  {availableFabrics.map((fabric) => (
                    <button
                      key={fabric}
                      onClick={() => setSelectedFabric(fabric)}
                      className={`px-3 py-1 rounded-md border text-sm transition-all ${
                        selectedFabric === fabric
                          ? "border-gray-800 bg-gray-800 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-800"
                      }`}
                    >
                      {fabric}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {availableGenders.length > 0 && (
              <div className="flex items-center mb-4">
                <h5 className="text-lg font-semibold me-2">Gender:</h5>
                <div className="flex space-x-2">
                  {availableGenders.map((gender) => (
                    <button
                      key={gender}
                      onClick={() => setSelectedGender(gender)}
                      className={`px-3 py-1 rounded-md border text-sm transition-all ${
                        selectedGender === gender
                          ? "border-gray-800 bg-gray-800 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-800"
                      }`}
                    >
                      {gender}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        <div className="p-3 sm:p-4 rounded-md shadow-sm text-xs sm:text-sm text-gray-800">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 640 512"
              className="w-4 sm:w-5 h-4 sm:h-5"
            >
              <path d="M0 128C0 92.65 28.65 64 64 64H576C611.3 64 640 92.65 640 128V384C640 419.3 611.3 448 576 448H64C28.65 448 0 419.3 0 384V128zM48 128V384C48 401.7 62.33 416 80 416H560C577.7 416 592 401.7 592 384V128C592 110.3 577.7 96 560 96H80C62.33 96 48 110.3 48 128zM112 160C112 142.3 126.3 128 144 128H496C513.7 128 528 142.3 528 160C528 177.7 513.7 192 496 192H144C126.3 192 112 177.7 112 160z" />
            </svg>
            <span className="font-medium text-gray-900">
              Check Product Availability
            </span>
          </div>
          <div className="flex gap-2 mb-2 sm:mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[100px] sm:min-w-[120px]">
              <input
                type="text"
                placeholder="Enter Pincode"
                value={pincode}
                onChange={handlePincodeChange}
                className="w-full border border-gray-400 rounded px-2 sm:px-3 py-1.5 sm:py-2 focus:outline-none focus:ring-1 focus:ring-black text-xs sm:text-sm"
                maxLength={6}
              />
              {pincode.length === 6 && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {isPincodeValid ? (
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  ) : pincodeError ? (
                    <svg
                      className="w-5 h-5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  ) : null}
                </div>
              )}
            </div>
            <button
              onClick={handleCheckPincode}
              className="bg-black text-white hover:text-green-500 px-3 sm:px-4 rounded hover:bg-gray-800 text-xs sm:text-sm disabled:opacity-50"
              disabled={pincode.length !== 6 || isLoadingPincode}
            >
              {isLoadingPincode ? "Checking..." : "Check Now"}
            </button>
          </div>

          {pincodeError && (
            <div className="text-red-500 text-xs sm:text-sm mt-1">
              {pincodeError}
            </div>
          )}

          {availabilityInfo && (
            <div className="mt-2 text-xs sm:text-sm">
              {availabilityInfo.error ? (
                <div className="text-red-500">{availabilityInfo.error}</div>
              ) : (
                <div className="text-green-500">
                  <p>
                    {availabilityInfo.name}, {availabilityInfo.district},{" "}
                    {availabilityInfo.state}, {availabilityInfo.country}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        {productCombinations.length > 0 && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-lg font-semibold text-gray-900">
                Product Combinations
              </h5>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {productCombinations.map((combination) => (
                <div
                  key={combination.id}
                  className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: combination.colorValue }}
                    ></div>
                    {imageType === "custom" ? (
                      <span className="text-xs sm:text-sm text-gray-700">
                        {combination.size}{" "}
                      </span>
                    ) : (
                      <span className="text-xs sm:text-sm text-gray-700">
                        {combination.color}, {combination.size},{" "}
                        {combination.fabric}, {combination.gender}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      {combination.quantity}
                    </span>
                    <button
                      onClick={() => handleRemoveCombination(combination.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiX className="size-3 sm:size-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={(e) => handleAddToCart(e, true)}
              className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-black text-white hover:text-green-500 font-medium hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!isPincodeValid}
            >
              <svg
                className="w-3 h-3 sm:w-4 sm:h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span>Add All to Cart</span>
            </button>
          </div>
        )}
        {productCombinations.length === 0 && (
          <button
            type="button"
            onClick={handleAddToCart}
            className="w-full flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-3 rounded-md bg-black text-white hover:text-green-500 font-medium hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isPincodeValid}
          >
            <svg
              className="w-4 sm:w-5 h-4 sm:h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <span>Add to Cart</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {renderHeader()}
      <section className="md:py-16 py-8 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 grid-cols-1 gap-6 sm:gap-8">
            {renderImageSection()}
            {renderDetailsSection()}
          </div>
        </div>
      </section>
      <BottomNav />
      <Footer />
      <Switcher />
      <ScrollToTop />
    </>
  );
}
